/**
 * APIエラーハンドリングユーティリティ
 *
 * バックエンドAPIからのエラーレスポンスを統一的に処理する関数群。
 * すべてのエラーメッセージは日本語で提供される。
 */

/** 入力の検査の失敗（サーバの validation_exception_handler が返す） */
export const INPUT_VALIDATION_ERROR_STATUS = 422;

/**
 * エラー応答の detail のうち、利用者に出してよいものを返す
 *
 * detail は空でない文字列のときだけ出す。入力の検査の 422 は、detail が文字列でも出さない
 * (サーバは 422 の detail に、検査の道具の英語の文言をつないだ文字列を入れて返す。利用者に出す文言ではない)
 *
 * @param {number} status - 応答の状態コード
 * @param {Object|null|undefined} errorData - 応答の本文（JSON を読めなかったときは null 等）
 * @returns {string|null} 出してよい detail。出せないときは null
 */
export function userFacingDetail(status, errorData) {
    if (status === INPUT_VALIDATION_ERROR_STATUS) return null;
    const detail = errorData?.detail;
    return typeof detail === 'string' && detail ? detail : null;
}

/**
 * APIエラーを統一的に処理する関数（シンプル版）
 *
 * @param {Response} response - fetch APIのレスポンス
 * @param {string} defaultMessage - デフォルトエラーメッセージ（日本語）
 * @returns {Promise<string>} エラーメッセージ
 */
export async function handleApiError(
    response,
    defaultMessage = "エラーが発生しました。"
) {
    try {
        const errorData = await response.json();
        return userFacingDetail(response.status, errorData) || defaultMessage;
    } catch {
        // JSONパース失敗時はデフォルトメッセージを返す
        return defaultMessage;
    }
}

/**
 * エラータイプに応じた処理を行う拡張版
 *
 * HTTPステータスコードに応じたコールバックを実行しつつ、
 * エラーメッセージを取得する。
 *
 * @param {Response} response - fetch APIのレスポンス
 * @param {Object} options - オプション
 * @param {string} options.defaultMessage - デフォルトメッセージ
 * @param {Function} options.onNotFound - 404エラー時のコールバック
 * @param {Function} options.onUnauthorized - 401エラー時のコールバック
 * @param {Function} options.onForbidden - 403エラー時のコールバック
 * @returns {Promise<string>} エラーメッセージ
 *
 * @example
 * const errorMessage = await handleApiErrorAdvanced(response, {
 *   defaultMessage: '更新に失敗しました。',
 *   onNotFound: () => setShowNotFoundModal(true),
 *   onUnauthorized: () => navigate('/login'),
 * });
 */
export async function handleApiErrorAdvanced(response, options = {}) {
    const {
        defaultMessage = "エラーが発生しました。",
        onNotFound,
        onUnauthorized,
        onForbidden,
    } = options;

    // ステータスコードベースのコールバック実行
    if (response.status === 404 && onNotFound) {
        onNotFound();
    } else if (response.status === 401 && onUnauthorized) {
        onUnauthorized();
    } else if (response.status === 403 && onForbidden) {
        onForbidden();
    }

    // エラーメッセージ取得
    try {
        const errorData = await response.json();
        return userFacingDetail(response.status, errorData) || defaultMessage;
    } catch {
        return defaultMessage;
    }
}
